import { ItemType } from "@/types/enums/item-type";
import type { PatientPortalMembershipDiscount } from "@/types/patient-portal";
import type { ServiceDetail } from "@/types/service";
import type { PackageIncludedItem } from "@/types/shop";

export interface PostSignupBenefitTarget {
  itemId: string | null;
  itemType: string | null;
}

export interface PostSignupServiceBenefit {
  serviceName: string | null;
}

export const membershipDiscountsToBenefitTargets = (
  discounts: PatientPortalMembershipDiscount[] | null | undefined,
): PostSignupBenefitTarget[] =>
  (discounts ?? []).map(({ itemId, itemType }) => ({ itemId, itemType }));

export const packageIncludedItemsToBenefitTargets = (
  items: PackageIncludedItem[] | null | undefined,
): PostSignupBenefitTarget[] =>
  (items ?? []).map(({ itemId }) => ({ itemId, itemType: null }));

/**
 * Resolve concrete benefits that point at services exposed by self-scheduling.
 * A single unique service can be named in the CTA; multiple services use
 * generic appointment copy. Broad service/category benefits are ignored.
 */
export const selectPostSignupServiceBenefit = (
  targets: PostSignupBenefitTarget[],
  services: Pick<ServiceDetail, "id" | "name">[],
): PostSignupServiceBenefit | null => {
  const servicesById = new Map(
    services.map((service) => [service.id, service]),
  );

  const matchedServices = new Map<string, string>();
  for (const target of targets) {
    if (!target.itemId) continue;
    if (target.itemType && target.itemType !== ItemType.SERVICE) continue;

    const service = servicesById.get(target.itemId);
    if (service) matchedServices.set(service.id, service.name);
  }

  if (matchedServices.size === 0) return null;
  return {
    serviceName:
      matchedServices.size === 1
        ? matchedServices.values().next().value!
        : null,
  };
};

export const postSignupSelfScheduleHref = (tenantSlug: string): string =>
  `/${encodeURIComponent(tenantSlug)}/self-schedule`;
